/** Статус ошибки без ответа сервера: сеть недоступна или истёк таймаут. */
export const NETWORK_ERROR_STATUS = 0;

export const API_ERROR_MESSAGES = {
    network: "Сервер недоступен. Проверьте подключение и повторите попытку",
    timeout: "Сервер не ответил вовремя. Повторите попытку",
    invalidResponse: "Некорректный ответ сервера",
    400: "Проверьте введённые данные",
    401: "Сессия истекла. Войдите снова",
    403: "Недостаточно прав",
    404: "Не найдено или нет доступа",
    409: "Данные изменились. Обновите страницу и повторите",
    413: "Файл слишком большой",
    415: "Недопустимый тип файла",
    server: "Ошибка сервера. Повторите попытку позже",
    unknown: "Не удалось выполнить запрос",
} as const;

export type ApiErrorKind = "http" | "network" | "timeout";

/** Текст по статусу, когда сервер не прислал свой. */
export function defaultMessageForStatus(status: number): string {
    switch (status) {
        case 400:
        case 401:
        case 403:
        case 404:
        case 409:
        case 413:
        case 415:
            return API_ERROR_MESSAGES[status];
        default:
            return status >= 500 ? API_ERROR_MESSAGES.server : API_ERROR_MESSAGES.unknown;
    }
}

/**
 * Ошибка запроса к API. `message` всегда можно показать пользователю: это текст сервера
 * (`{"code", "message"}`) или общий текст по статусу, без внутренних деталей.
 */
export class ApiError extends Error {
    override readonly name = "ApiError";
    readonly kind: ApiErrorKind;
    /** HTTP-статус; `0` — ответа нет (сеть, таймаут). */
    readonly status: number;
    /** `code` из тела ошибки сервера (HTTP-статус строкой) или статус, если тела нет. */
    readonly code: string;
    readonly boardTitle?: string;

    constructor({
        status,
        message,
        code = String(status),
        kind = "http",
        cause,
        boardTitle,
    }: {
        status: number;
        message: string;
        code?: string;
        kind?: ApiErrorKind;
        cause?: unknown;
        boardTitle?: string;
    }) {
        super(message, { cause });
        this.kind = kind;
        this.status = status;
        this.code = code;
        this.boardTitle = boardTitle;
    }

    static network(cause?: unknown): ApiError {
        return new ApiError({
            status: NETWORK_ERROR_STATUS,
            message: API_ERROR_MESSAGES.network,
            code: "network",
            kind: "network",
            cause,
        });
    }

    static timeout(cause?: unknown): ApiError {
        return new ApiError({
            status: NETWORK_ERROR_STATUS,
            message: API_ERROR_MESSAGES.timeout,
            code: "timeout",
            kind: "timeout",
            cause,
        });
    }

    get isUnauthorized(): boolean {
        return this.status === 401;
    }

    get isForbidden(): boolean {
        return this.status === 403;
    }

    /** 404. Сервер отдаёт его и для недоступного ресурса, если само существование скрыто. */
    get isNotFound(): boolean {
        return this.status === 404;
    }

    /** 409 — бизнес-конфликт: текст сервера нужно показать пользователю. */
    get isConflict(): boolean {
        return this.status === 409;
    }

    /** 400 — сервер отклонил данные запроса. */
    get isValidation(): boolean {
        return this.status === 400;
    }

    /** Ответа нет: сеть недоступна или истёк таймаут. */
    get isNetwork(): boolean {
        return this.kind !== "http";
    }

    get isTimeout(): boolean {
        return this.kind === "timeout";
    }

    get isServerError(): boolean {
        return this.status >= 500;
    }
}

export function isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
}

/** Отмена запроса (`signal` React Query, размонтирование) — не ошибка для пользователя. */
export function isAbortError(error: unknown): boolean {
    // По имени, а не instanceof: DOMException не везде наследует Error того же realm.
    return typeof error === "object" && error !== null && (error as { name?: unknown }).name === "AbortError";
}

function readServerMessage(body: string): { code?: string; message?: string; boardTitle?: string } {
    if (!body) return {};
    try {
        const parsed: unknown = JSON.parse(body);
        if (typeof parsed !== "object" || parsed === null) return {};
        const { code, message, boardTitle } = parsed as Record<string, unknown>;
        return {
            code: typeof code === "string" && code ? code : undefined,
            message: typeof message === "string" && message.trim() ? message.trim() : undefined,
            boardTitle: typeof boardTitle === "string" ? boardTitle : undefined,
        };
    } catch {
        return {};
    }
}

/**
 * Ошибка из ответа сервера. Тело разбирается безопасно: не JSON, без `message` или пустое — общий текст
 * по статусу. Для 401 и 5xx текст сервера не показывается: это технические сообщения
 * (`Token verification error`, внутренние ошибки), а не тексты для пользователя.
 */
export function apiErrorFromResponse(status: number, body: string): ApiError {
    const server = readServerMessage(body);
    const useServerMessage = status !== 401 && status < 500;
    return new ApiError({
        status,
        code: server.code ?? String(status),
        boardTitle: status === 403 && server.message === "board_password_required" ? server.boardTitle : undefined,
        message: (useServerMessage ? server.message : undefined) ?? defaultMessageForStatus(status),
    });
}

/** Текст ошибки для пользователя: `message` у `ApiError`, общий текст для прочих ошибок. */
export function getErrorMessage(error: unknown): string {
    if (isApiError(error)) return error.message;
    return API_ERROR_MESSAGES.unknown;
}
